import React, { forwardRef, useEffect, useId, useRef } from "react";

import "../../styles/field.scss";
import "./Input.scss";

interface Props
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix" | "value"> {
  /** Deliberately loose: call sites across the app hand this a null. */
  value?: any;
  label?: React.ReactNode;
  /** Sits under the field. Turns red with the rest of it when error is set. */
  helperText?: React.ReactNode;
  /** Colours the label, the border and the helper text. */
  error?: boolean;
  disabled?: boolean;
  /** Takes focus on mount, and again each time this flips back to true. */
  focus?: boolean;
  /** Trailing content inside the box - a unit, a phone number, an icon. */
  suffix?: React.ReactNode;
  /** Leading content inside the box. */
  prefix?: React.ReactNode;
  /** Goes on the outer wrapper; className goes on the input itself. */
  wrapperClassName?: string;
}

const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      helperText,
      error = false,
      disabled = false,
      focus = false,
      required = false,
      suffix,
      prefix,
      className = "",
      wrapperClassName = "",
      id,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const generatedId = useId();
    const inputId = id || rest.name || generatedId;
    const helperId = `${inputId}-helper`;

    // Keyed on the prop rather than run once, so a caller can pull focus back
    // to the field later - after a failed submit, say.
    useEffect(() => {
      if (focus && !disabled) inputRef.current?.focus();
    }, [focus, disabled]);

    return (
      <div
        className={[
          "fld",
          error ? "fld-error" : "",
          disabled ? "fld-disabled" : "",
          // Legacy hook. A dozen stylesheets still size this component with
          // rules like `.food-items .search_bar { width: 90% }`; dropping the
          // class would silently reflow those screens.
          "search_bar",
          wrapperClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {label && (
          <label className="fld-label" htmlFor={inputId}>
            {label}
            {required && <span className="fld-required">*</span>}
          </label>
        )}

        <div className="fld-box">
          {prefix && <span className="fld-affix">{prefix}</span>}
          <input
            {...rest}
            id={inputId}
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            className={`fld-input input-box ${className}`.trim()}
            disabled={disabled}
            required={required}
            aria-invalid={error || undefined}
            aria-describedby={helperText ? helperId : undefined}
            // A number field that scrolls under the cursor silently changes the
            // value the coach just typed.
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
          />
          {suffix && <span className="fld-affix">{suffix}</span>}
        </div>

        {helperText && (
          <div className="fld-helper" id={helperId}>
            {helperText}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
